import type { OfflineBundle } from "./api.js";

// Data v zařízení (REQ-019, REQ-021, NFR-003): vlastnické tokeny sezení, odpovědi, odkazy na
// výsledky a data pro náhled výsledku kola bez připojení.
// IndexedDB, když není k dispozici, localStorage. Zápisy jdou za sebou (fronta), aby rychlé
// klepnutí nepřepsalo novější stav starším.

export interface SessionRecord {
  readonly id: string;
  readonly token: string;
  readonly slug: string;
  readonly locale: string;
  readonly origin: "user" | "preview";
  readonly createdAt: string;
  /** Postup pro ikonu na domovské stránce (zadání 8.1). */
  answered: number;
  total: number;
  /** Token odkazu na výsledek, dostupný jen při vzniku (REQ-024). */
  resultToken?: string | null;
  done?: boolean;
  /** Kdy zařízení test dokončilo (REQ-041), u starších záznamů chybí. */
  finishedAt?: string;
  /** Sezení je připojené k identitě (REQ-033): pruh „neuložené výsledky“ ho nepočítá. */
  saved?: boolean;
}

/** Pozvánka do páru v zařízení (REQ-034): token z odkazu, kdo zve a test. */
export interface StoredInvite {
  readonly token: string;
  readonly inviter: string;
  readonly slug: string;
}

export interface LocalAnswer {
  readonly value: readonly number[];
  readonly clientSeq: number;
  readonly durationMs: number | null;
  readonly round: number;
  synced: boolean;
}

export type Answers = Record<string, LocalAnswer>;

interface Backend {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
}

const PREFIX = "mk:";

const local: Backend = {
  get: async (key) => {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? undefined : JSON.parse(raw);
  },
  set: async (key, value) => localStorage.setItem(PREFIX + key, JSON.stringify(value)),
  del: async (key) => localStorage.removeItem(PREFIX + key),
};

// Prohlížeč může spojení zavřít sám (smazání dat webu, tlak na úložiště, Firefox), pak každá
// transakce končí InvalidStateError. Další operace proto otevře spojení znovu.
function idb(): Promise<Backend> {
  let conn: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (conn !== null) return conn;
    const p = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open("mesketch", 1);
      req.onupgradeneeded = () => req.result.createObjectStore("kv");
      req.onerror = () => reject(req.error ?? new Error("indexeddb"));
      req.onsuccess = () => {
        const db = req.result;
        const drop = () => {
          if (conn === p) conn = null;
        };
        db.onclose = drop;
        db.onversionchange = () => {
          db.close();
          drop();
        };
        resolve(db);
      };
    });
    conn = p;
    p.catch(() => {
      if (conn === p) conn = null;
    });
    return p;
  };
  const transaction = async (mode: IDBTransactionMode): Promise<IDBTransaction> => {
    const p = open();
    try {
      return (await p).transaction("kv", mode);
    } catch (e) {
      if ((e as { name?: unknown } | null)?.name !== "InvalidStateError") throw e;
      if (conn === p) conn = null;
      return (await open()).transaction("kv", mode);
    }
  };
  const run = async <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest) => {
    const tx = await transaction(mode);
    return new Promise<T>((ok, fail) => {
      const r = fn(tx.objectStore("kv"));
      r.onsuccess = () => ok(r.result as T);
      r.onerror = () => fail(r.error ?? new Error("indexeddb"));
    });
  };
  return open().then(() => ({
    get: (key) => run("readonly", (s) => s.get(key)),
    set: async (key, value) => void (await run("readwrite", (s) => s.put(value, key))),
    del: async (key) => void (await run("readwrite", (s) => s.delete(key))),
  }));
}

let backend: Promise<Backend> | null = null;
function store(): Promise<Backend> {
  backend ??= (typeof indexedDB === "undefined" ? Promise.reject(new Error("no indexeddb")) : idb()).catch(() => local);
  return backend;
}

let queue: Promise<unknown> = Promise.resolve();
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const next = queue.then(fn, fn);
  queue = next.catch(() => undefined);
  return next;
}

const get = <T>(key: string) => serial(async () => (await (await store()).get(key)) as T | undefined);
const set = (key: string, value: unknown) => serial(async () => (await store()).set(key, value));
const del = (key: string) => serial(async () => (await store()).del(key));

export const device = {
  async sessions(): Promise<SessionRecord[]> {
    const ids = (await get<string[]>("index")) ?? [];
    const out: SessionRecord[] = [];
    for (const id of ids) {
      const s = await get<SessionRecord>(`session:${id}`);
      if (s !== undefined) out.push(s);
    }
    return out;
  },
  session: (id: string) => get<SessionRecord>(`session:${id}`),
  async saveSession(s: SessionRecord): Promise<void> {
    await set(`session:${s.id}`, s);
    const ids = (await get<string[]>("index")) ?? [];
    if (!ids.includes(s.id)) await set("index", [...ids, s.id]);
  },
  async removeSession(id: string): Promise<void> {
    await del(`session:${id}`);
    await del(`answers:${id}`);
    await del(`offline:${id}`);
    const ids = (await get<string[]>("index")) ?? [];
    await set(
      "index",
      ids.filter((i) => i !== id),
    );
  },
  answers: async (id: string) => (await get<Answers>(`answers:${id}`)) ?? {},
  saveAnswers: (id: string, answers: Answers) => set(`answers:${id}`, answers),
  offline: (id: string) => get<OfflineBundle>(`offline:${id}`),
  saveOffline: (id: string, bundle: OfflineBundle) => set(`offline:${id}`, bundle),
  previewSession: (hash: string) => get<string>(`preview:${hash}`),
  setPreviewSession: (hash: string, id: string) => set(`preview:${hash}`, id),
  async byResultToken(token: string): Promise<SessionRecord | undefined> {
    return (await device.sessions()).find((s) => s.resultToken === token);
  },
  // Páry (REQ-034): token pozvánky z adresy (adresa ho hned ztratí), pozvánka sezení založeného
  // z pozvánky, přijatá pozvánka → pár (přesměrování) a pár → sezení člena (čtení bez identity).
  pendingInvite: () => get<StoredInvite>("invite:pending"),
  setPendingInvite: (invite: StoredInvite) => set("invite:pending", invite),
  clearPendingInvite: () => del("invite:pending"),
  sessionInvite: (sessionId: string) => get<StoredInvite>(`invite:session:${sessionId}`),
  setSessionInvite: (sessionId: string, invite: StoredInvite) => set(`invite:session:${sessionId}`, invite),
  clearSessionInvite: (sessionId: string) => del(`invite:session:${sessionId}`),
  async acceptedPair(token: string): Promise<string | undefined> {
    return get<string>(`invite:pair:${await sha256Hex(token)}`);
  },
  async setAcceptedPair(token: string, pairId: string): Promise<void> {
    await set(`invite:pair:${await sha256Hex(token)}`, pairId);
  },
  pairSession: (pairId: string) => get<string>(`pair:session:${pairId}`),
  setPairSession: (pairId: string, sessionId: string) => set(`pair:session:${pairId}`, sessionId),
};

/** SHA-256 v hex (klíč náhledu v zařízení, token odkazu se neukládá čitelně). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
